import { useEffect, useState, useRef } from "react";
import { useGlobalContext } from "../Context/GlobalContext";
import { useDeviceContext } from "../Context/DeviceContext";
import CircularSlider from "react-circular-slider-svg";
import { Grid2 as Grid } from '@mui/material';
import Button from "../Components/button";
import { FlashPattern } from "../Types";
import { hasCobberSettings } from "../Lib/Devices";

const Flash = (
    {
        pattern,
        multipliers,
        type,
        controlled,
        tilt,
        hasBrake,
        brakeMode,
    }: {
        pattern: any,
        multipliers?: { brightness: number, speed: number },
        type: number,
        controlled?: boolean
        tilt?: boolean
        hasBrake?: boolean
        brakeMode?: number
    }
) => {

    const { getThemeImage, getThemeStyles } = useGlobalContext();
    const { cartesianPatterns, patterns } = useDeviceContext();

    const lightImgs: any = {
        1: getThemeImage("light"),
        3: getThemeImage("light_cobber_channel"),
        4: getThemeImage("light"),
        5: getThemeImage("light_large"),
        6: getThemeImage("light_large"),
        7: getThemeImage("light_cobber_channel"),
        8: getThemeImage("light_cobber_channel"),
        10: getThemeImage("light_cobber_channel"),
        11: getThemeImage("light_cobber_channel"),
    }

    const backgroundImgs: any = {
        1: getThemeImage("background"),
        3: getThemeImage("background_cobber_channel"),
        4: getThemeImage("background"),
        5: getThemeImage("background_large"),
        6: getThemeImage("background_large"),
        7: getThemeImage("background_cobber_channel"),
        8: getThemeImage("background_cobber_channel"),
        10: getThemeImage("background_cobber_channel"),
        11: getThemeImage("background_cobber_channel"),
    }

    const xModeLength = (pattern as unknown as any)?.x_patterns ? (pattern as unknown as any).x_patterns?.length : 1;
    const styles = getThemeStyles();

    const [currentBrightness, setCurrentBrightness] = useState<number[]>([]);
    const [hovering, setHovering] = useState(false);
    const [braking, setBraking] = useState(false);
    const [patternNum, setPatternNum] = useState((xModeLength + 1) / 2);
    const [autoPlay, setAutoPlay] = useState(!hasCobberSettings(type));
    const patternRef = useRef<number>((xModeLength + 1) / 2);
    const brakingRef = useRef(false);
    const brightnessRef = useRef(1);

    const handleBrake = async () => {
        const pattern = getPattern('z');
        if(!pattern) return;

        setBraking(!braking);
        brakingRef.current = !braking;

        for (let i = 0; i < pattern.length; i++) {
            const command = pattern[i];
            command.channels.sort((a: any, b: any) => a.channel - b.channel);
            setCurrentBrightness(command.channels.map((c: any) => Math.min(1, c.brightness * (brightnessRef.current || 1))));
    
            await new Promise(resolve => setTimeout(resolve, command.duration));
        }
    };

    const handlePatternChange = (value: number) => {
        setPatternNum(value);
        patternRef.current = value;
    }

    useEffect(() => {
        brightnessRef.current = multipliers?.brightness ?? 1
    }, [multipliers?.brightness]);

    useEffect(() => {
        if (!controlled && !hovering) return;
    
        let cancelled = false;
    
        const runSnakeLoop = async () => {
            const allPatterns = getPattern('x');
            if(!allPatterns) return;
            const uniqueIndexes = Array.from(new Set(allPatterns.map(p => p.index)));
            let forward = true;
            let safety = 0;

            while (!cancelled) {
                safety++;
                if(safety > 1000) break;
                const indexes = forward ? uniqueIndexes : [...uniqueIndexes].reverse();
                forward = !forward;

                for (const index of indexes) {
                    if (cancelled) break;
                    
                    if(autoPlay) setPatternNum(index + 1);

                    const filteredPattern = allPatterns.filter(
                        p => p.index === (
                            (autoPlay || !controlled) ? xModeLength - index - 1 : (xModeLength - patternRef.current!)
                        )
                    );

                    if (filteredPattern.length === 0) break;

                    for (const command of filteredPattern) {
                        if (cancelled) break;
                        if (!brakingRef.current) {
                            command.channels.sort((a: any, b: any) => a.channel - b.channel);
                            setCurrentBrightness(command.channels.map((c: any) => Math.min(1, c.brightness ? (0.6 + (c.brightness * brightnessRef.current)) * 0.6 : 0)));
                        }

                        await new Promise(resolve =>
                            setTimeout(resolve, (command.duration || 1000) / (multipliers?.speed || 1))
                        );
                    }
                    
                    if(xModeLength > 1) await new Promise(resolve => setTimeout(resolve, 500));
                }
            }
        };
    
        runSnakeLoop();
    
        return () => {
            cancelled = true;
            setCurrentBrightness([])
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hovering, controlled, multipliers?.speed, autoPlay, patternRef.current, pattern]);

    const getPattern = (patternType: string) => {
        if(!pattern) return [];
        let showPattern = [];
        if(cartesianPatterns) {
            if(patternType === 'z') showPattern = [patterns.find((p: any) => p.id === brakeMode)?.pattern];
            else showPattern = pattern.x_patterns;
        } else {
            showPattern = [pattern];
        }

        if(!showPattern || !showPattern[0]) return null;
        let flashPattern: FlashPattern[] = [];
                                                                            
        showPattern.forEach((commands: any, index: number) => {
            if(hasCobberSettings(type)) {
                let channels: any[] = [...[...Array(5)].map((_, index) => ({ channel: index + 1, brightness: 0 }))];
                let currChannels: any = [];
                let steady = true;

                for (let i = 0; i < commands.length; i++) {
                    if(commands[i].command === "channelSelect") {
                        currChannels = [];
                        for (let j = 0; j < commands[i].data.length; j++) {
                            currChannels.push({channel: commands[i].data[j], brightness: 0});
                        }
                    } else if(commands[i].command === "PWM") {
                        currChannels.forEach((curr: any) => {
                            const channel = channels.find((channel: any) => channel.channel === curr.channel);
                            const brightness = commands[i].data > 0 ? commands[i].data / 100 : 0
                                
                            if(channel) channel.brightness = brightness;
                            else channels.push({ ...curr, brightness });
                        });
                    } else if(commands[i].command === "timeByte") {
                        flashPattern.push({duration: commands[i].data, channels: JSON.parse(JSON.stringify(channels)), index});
                        steady = false;
                    }
                }
                if(steady) flashPattern.push({duration: 500, channels, index});
            } else {
                let lastTimeBtye = 0;

                for (let i = 0; i < commands.length; i++) {
                    if(commands[i].command === "PWM") {
                        const brightness = commands[i].data > 0 ? commands[i].data / 100 : 0
                        flashPattern.push({duration: lastTimeBtye, channels: [{channel: 0, brightness}], index});
                    } else if(commands[i].command === "timeByte") {
                        lastTimeBtye = commands[i].data
                    }
                }
            }
        });

        return flashPattern;
    };

    return (
        <div onClick={() => setHovering(false)} >
            <Grid container>
                <Grid size={{ xs: tilt ? 3 : 12 }} style={controlled ? {} : {width: '100%'}}>
                    <div
                        className="flash-container"
                        onMouseEnter={() => { setHovering(!controlled) }}
                        onMouseLeave={() => { setHovering(false) }}
                        style={{
                            ...(hasCobberSettings(type) ? {} : { backgroundImage: `url(${backgroundImgs[type]})` }),
                            ...(controlled ? {width: hasCobberSettings(type) && tilt ? '120px' : '200px'} : {})
                        }}
                    >
                        {hasCobberSettings(type) ?
                            <div>
                                {[...Array(5)].map((_, index) => (
                                    <div
                                        key={index}
                                        style={{
                                            width: '20%',
                                            display: 'inline-block',
                                            position: 'relative',
                                            backgroundImage: `url(${backgroundImgs[type]})`,
                                            backgroundSize: 'cover'
                                        }}
                                    >
                                        <img
                                            src={lightImgs[type]}
                                            alt="light"
                                            style={{
                                                opacity: currentBrightness[index] ?? 0,
                                                zIndex: -1
                                            }}
                                        />
                                    </div>
                                ))}
                            </div>
                            : <img src={lightImgs[type]} alt="light" style={{ opacity: currentBrightness[0] ?? 0 }} />
                        }
                    </div>
                </Grid>
                {tilt && controlled &&
                    <Grid size={{ xs: 6 }} style={{ cursor: 'pointer', position: 'relative', marginTop: '-10px' }}>
                        <CircularSlider
                            size={175}
                            trackWidth={4}
                            coerceToInt={true}
                            minValue={1}
                            maxValue={xModeLength ?? 1}
                            startAngle={115}
                            endAngle={245}
                            angleType={{
                                direction: "cw",
                                axis: "-y"
                            }}
                            handle1={{
                                value: patternNum,
                                onChange: v => {if(!autoPlay) handlePatternChange(v)},
                                color: styles['--theme-color']
                            }}
                            arcColor={styles['--theme-color']}
                            arcBackgroundColor={styles['--theme-color']}
                        />
                        <img
                            src={getThemeImage('bike')}
                            alt="bike"
                            className="rotate-bike"
                            style={{
                                transform: `rotate(${(((patternNum) * 2 - 1) - xModeLength) * (50 / xModeLength)}deg)`,
                                transformOrigin: '50% 100%'
                            }}
                        />
                    </Grid>}
                {controlled && (tilt || hasBrake) && 
                    <Grid size={{ xs: 3 }} sx={!tilt ? { position: 'absolute', top: '-40px', right: '165px'} : {}}>
                        <div style={{marginTop: '50px'}}>
                            {tilt && controlled && cartesianPatterns && <Button
                                text={autoPlay ? 'Stop Play' : 'Autoplay'}
                                onClick={() => setAutoPlay(!autoPlay)}
                                className={`playing-button`}
                            />}
                            {hasBrake && brakeMode !== 0 && <Button
                                text={braking ? 'Stop Brake' : 'Brake'}
                                onClick={handleBrake}
                                className={`brake-button ${braking ? 'filled' : ''}`}
                            />}
                        </div>
                    </Grid>}
            </Grid>
        </div>
    );
};

export default Flash;